/**
 * BwiAggregateCard
 * ================
 * Displays the cross-visit Behaviour Wellness & Impact aggregate in the
 * Historical Summary tab of the Right Panel.
 *
 * Three sections:
 *   A. Aggregate summary — average overall score, factor averages, range, trend
 *   B. Visit trend chart — inline SVG sparkline of overall_score per visit
 *   C. Comparison table — per-visit factor breakdown with delta indicators
 */
import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoAwesome,
  ExpandLess,
  ExpandMore,
  Refresh,
  TrendingDown,
  TrendingFlat,
  TrendingUp,
} from '@mui/icons-material';
import type { BwiAggregate, BwiVisitEntry } from '../../hooks/useBwiAggregate';
import { useBwiAggregate } from '../../hooks/useBwiAggregate';

// ──────────────────────────────────────────────────────────────────────────────
// HELPERS
// ──────────────────────────────────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score <= 20) return '#4caf50';
  if (score <= 45) return '#ff9800';
  if (score <= 70) return '#f44336';
  return '#b71c1c';
}

function trendIcon(direction: string) {
  if (direction === 'improving') return <TrendingDown sx={{ fontSize: 16, color: '#4caf50' }} />;
  if (direction === 'worsening') return <TrendingUp sx={{ fontSize: 16, color: '#f44336' }} />;
  if (direction === 'stable') return <TrendingFlat sx={{ fontSize: 16, color: '#ff9800' }} />;
  return null;
}

function trendLabel(direction: string): { text: string; color: string } {
  if (direction === 'improving') return { text: 'Improving', color: '#4caf50' };
  if (direction === 'worsening') return { text: 'Worsening', color: '#f44336' };
  if (direction === 'stable') return { text: 'Stable', color: '#ff9800' };
  return { text: 'Insufficient data', color: '#9e9e9e' };
}

function delta(current: number, previous: number | undefined): string {
  if (previous === undefined) return '–';
  const d = current - previous;
  if (d === 0) return '0';
  return d > 0 ? `+${d}` : `${d}`;
}

function deltaColor(current: number, previous: number | undefined): string {
  if (previous === undefined) return '#9e9e9e';
  const d = current - previous;
  if (d > 5) return '#f44336';   // worse
  if (d < -5) return '#4caf50';  // better
  return '#9e9e9e';
}

// ──────────────────────────────────────────────────────────────────────────────
// SPARKLINE (pure SVG, no external chart lib)
// ──────────────────────────────────────────────────────────────────────────────

function Sparkline({ visits }: { visits: BwiVisitEntry[] }) {
  if (visits.length < 2) return null;
  const W = 280, H = 56, PAD = 8;
  const scores = visits.map((v) => v.overall_score);
  const minS = Math.min(...scores, 0);
  const maxS = Math.max(...scores, 100);
  const range = maxS - minS || 1;

  const toX = (i: number) => PAD + (i / (visits.length - 1)) * (W - PAD * 2);
  const toY = (s: number) => H - PAD - ((s - minS) / range) * (H - PAD * 2);

  const points = visits.map((v, i) => `${toX(i)},${toY(v.overall_score)}`).join(' ');
  const area = `${PAD},${H - PAD} ${points} ${W - PAD},${H - PAD}`;

  return (
    <Box sx={{ mt: 1, mb: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#444', display: 'block', mb: 0.5 }}>
        Overall Score Trend
      </Typography>
      <svg width={W} height={H} style={{ display: 'block', overflow: 'visible' }}>
        {/* Horizontal grid lines */}
        {[0, 25, 50, 75, 100].map((g) => (
          <line
            key={g}
            x1={PAD} y1={toY(g)} x2={W - PAD} y2={toY(g)}
            stroke="#f0f0f0" strokeWidth={1}
          />
        ))}
        {/* Filled area */}
        <polygon points={area} fill="#5c6bc022" />
        {/* Line */}
        <polyline points={points} fill="none" stroke="#5c6bc0" strokeWidth={2} strokeLinejoin="round" />
        {/* Data points */}
        {visits.map((v, i) => (
          <Tooltip key={i} title={`${v.visit_date ?? 'Visit ' + (i + 1)}: ${v.overall_score}`}>
            <circle
              cx={toX(i)} cy={toY(v.overall_score)} r={4}
              fill={scoreColor(v.overall_score)}
              stroke="#fff" strokeWidth={1.5}
              style={{ cursor: 'pointer' }}
            />
          </Tooltip>
        ))}
        {/* X-axis labels */}
        {visits.map((v, i) => (
          <text
            key={i}
            x={toX(i)} y={H}
            textAnchor="middle"
            fontSize={9}
            fill="#aaa"
          >
            {v.visit_date ? v.visit_date.slice(5) : `V${i + 1}`}
          </text>
        ))}
      </svg>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// FACTOR AVERAGE ROW
// ──────────────────────────────────────────────────────────────────────────────

function AvgFactorRow({ label, weight, score }: { label: string; weight: string; score: number }) {
  const color = scoreColor(score);
  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#444' }}>
          {label}
          <Typography component="span" variant="caption" sx={{ color: '#888', ml: 0.5 }}>({weight})</Typography>
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color, minWidth: 26, textAlign: 'right' }}>
          {score.toFixed(1)}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={score}
        sx={{
          height: 5,
          borderRadius: 3,
          bgcolor: '#e0e0e0',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
        }}
      />
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ──────────────────────────────────────────────────────────────────────────────

interface Props {
  patientId: number | null;
}

export default function BwiAggregateCard({ patientId }: Props) {
  const { aggregate, comparison, loading, recomputing, error, recompute } = useBwiAggregate(patientId);
  const [expanded, setExpanded] = useState(true);
  const [showTable, setShowTable] = useState(false);

  if (!patientId) return null;

  if (loading) {
    return (
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Loading Behaviour Wellness aggregate…
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!aggregate || aggregate.visit_count === 0) {
    return (
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2, borderStyle: 'dashed' }}>
        <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            No validated Behaviour Wellness records yet. Validate a visit index to see aggregated trends.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const trend = trendLabel(aggregate.trend_direction);
  const overallColor = scoreColor(aggregate.avg_overall_score);

  return (
    <Card
      variant="outlined"
      sx={{ mb: 2, borderRadius: 2, borderColor: '#c5cae9' }}
    >
      <CardContent sx={{ pb: '12px !important' }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesome sx={{ fontSize: 16, color: '#5c6bc0' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#012676' }}>
              Behaviour Wellness & Impact — All Visits
            </Typography>
            <Chip
              label={`${aggregate.visit_count} ${aggregate.visit_count === 1 ? 'Visit' : 'Visits'}`}
              size="small"
              sx={{ height: 18, fontSize: '10px', bgcolor: '#e8eaf6', color: '#3949ab' }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Recompute aggregate from all validated visits">
              <span>
                <IconButton size="small" onClick={recompute} disabled={recomputing}>
                  {recomputing ? <CircularProgress size={14} /> : <Refresh sx={{ fontSize: 16 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={() => setExpanded((p) => !p)}>
              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </Box>
        </Box>

        {/* Average overall score meter */}
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#333' }}>
              Average Overall Score
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {trendIcon(aggregate.trend_direction)}
              <Chip
                label={trend.text}
                size="small"
                sx={{ height: 18, fontSize: '10px', bgcolor: trend.color + '22', color: trend.color, fontWeight: 700 }}
              />
              <Typography variant="body2" sx={{ fontWeight: 800, color: overallColor, minWidth: 32, textAlign: 'right' }}>
                {aggregate.avg_overall_score.toFixed(1)} / 100
              </Typography>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={aggregate.avg_overall_score}
            sx={{
              height: 10, borderRadius: 5, bgcolor: '#e0e0e0',
              '& .MuiLinearProgress-bar': { bgcolor: overallColor, borderRadius: 5 },
            }}
          />
          {aggregate.highest_overall_score != null && aggregate.lowest_overall_score != null && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.3 }}>
              <Typography variant="caption" sx={{ color: '#888', fontSize: '10px' }}>
                Low: {aggregate.lowest_overall_score}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888', fontSize: '10px' }}>
                High: {aggregate.highest_overall_score}
              </Typography>
            </Box>
          )}
        </Box>

        <Collapse in={expanded}>
          <Divider sx={{ my: 1 }} />

          {/* Factor averages */}
          <AvgFactorRow label="Emotional Distress" weight="30%" score={aggregate.avg_emotional_distress} />
          <AvgFactorRow label="Symptom Burden"     weight="30%" score={aggregate.avg_symptom_burden} />
          <AvgFactorRow label="Affect Observed"    weight="20%" score={aggregate.avg_affect_observed} />
          <AvgFactorRow label="Risk Review"        weight="20%" score={aggregate.avg_risk_review} />

          {/* Sparkline */}
          {comparison.length >= 2 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Sparkline visits={comparison} />
            </>
          )}

          {/* Visit comparison table toggle */}
          {comparison.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setShowTable((p) => !p)}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#5c6bc0', flex: 1 }}>
                  Visit-by-Visit Comparison ({comparison.length})
                </Typography>
                {showTable ? <ExpandLess sx={{ fontSize: 16, color: '#5c6bc0' }} /> : <ExpandMore sx={{ fontSize: 16, color: '#5c6bc0' }} />}
              </Box>
              <Collapse in={showTable}>
                <Box sx={{ overflowX: 'auto', mt: 0.5 }}>
                  <Table size="small" sx={{ minWidth: 460 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontSize: '10px', fontWeight: 700, py: 0.5 }}>Date</TableCell>
                        <TableCell align="center" sx={{ fontSize: '10px', fontWeight: 700, py: 0.5 }}>Overall</TableCell>
                        <TableCell align="center" sx={{ fontSize: '10px', fontWeight: 700, py: 0.5 }}>Δ</TableCell>
                        <TableCell align="center" sx={{ fontSize: '10px', fontWeight: 700, py: 0.5 }}>ED</TableCell>
                        <TableCell align="center" sx={{ fontSize: '10px', fontWeight: 700, py: 0.5 }}>SB</TableCell>
                        <TableCell align="center" sx={{ fontSize: '10px', fontWeight: 700, py: 0.5 }}>AO</TableCell>
                        <TableCell align="center" sx={{ fontSize: '10px', fontWeight: 700, py: 0.5 }}>RR</TableCell>
                        <TableCell align="center" sx={{ fontSize: '10px', fontWeight: 700, py: 0.5 }}>Method</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comparison.map((v, i) => {
                        const prev = i > 0 ? comparison[i - 1].overall_score : undefined;
                        const d = delta(v.overall_score, prev);
                        const dc = deltaColor(v.overall_score, prev);
                        const oc = scoreColor(v.overall_score);
                        return (
                          <TableRow key={v.visit_id} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                            <TableCell sx={{ fontSize: '11px', py: 0.5 }}>
                              {v.visit_date ?? `Visit ${i + 1}`}
                            </TableCell>
                            <TableCell align="center" sx={{ py: 0.5 }}>
                              <Chip
                                label={`${v.overall_score} · ${v.overall_label}`}
                                size="small"
                                sx={{ height: 18, fontSize: '10px', bgcolor: oc + '22', color: oc, fontWeight: 700 }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '11px', fontWeight: 700, color: dc, py: 0.5 }}>
                              {d}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '11px', color: scoreColor(v.emotional_distress), py: 0.5 }}>
                              {v.emotional_distress}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '11px', color: scoreColor(v.symptom_burden), py: 0.5 }}>
                              {v.symptom_burden}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '11px', color: scoreColor(v.affect_observed), py: 0.5 }}>
                              {v.affect_observed}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '11px', color: scoreColor(v.risk_review), py: 0.5 }}>
                              {v.risk_review}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '10px', color: '#888', py: 0.5 }}>
                              {v.method}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
                <Typography variant="caption" sx={{ color: '#aaa', fontSize: '9px', display: 'block', mt: 0.5 }}>
                  ED = Emotional Distress · SB = Symptom Burden · AO = Affect Observed · RR = Risk Review · Δ = change from previous visit
                </Typography>
              </Collapse>
            </>
          )}

          {/* Disclaimer */}
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 1, color: '#bdbdbd', fontStyle: 'italic', fontSize: '10px' }}
          >
            ⚠ Aggregated from validated visits only. Assistive AI — does not replace clinical assessment.
          </Typography>
        </Collapse>

        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
